/** @jsx jsx */
import React, { type FC, useRef, useState, useMemo, type PropsWithChildren } from 'react';
import { styled, jsx } from '@compiled/react';
import { standardizeLocale } from '@jira/platform__supported-locales/src/supported-locales.tsx';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Section } from '@atlaskit/menu';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { metrics } from '@atlassian/browser-metrics';
import AsyncCreateDashboardGadgetModal from '@atlassian/jira-dashboard-create-dashboard-gadget-modal/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error/index.tsx';
import { FilterButton } from '@atlassian/jira-issue-navigator-actions-common/src/ui/filter-button/index.tsx';
import {
	isCustomFilter,
	isFilterId,
} from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { getOperationScope } from '@atlassian/jira-issue-table-common/src/utils/index.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { isAsyncExportItem, type AsyncExportIssuesCommonInput } from '../common/types.tsx';
import {
	getCreateDashboardGadgetModalParams,
	useCreateDashboardGadgetModalControls,
} from '../common/utils.tsx';
import { ExportIssuesState } from '../controllers/export-issues-state/index.tsx';
import { useFetchAllExportIssuesResources } from '../services/export-issues/index.tsx';
import { ECOSYSTEM_APPS_GROUP_INDEX } from './constants.tsx';
import { ExportItem } from './export-item/index.tsx';
import messages from './messages.tsx';
import type { ExportIssuesProps } from './types.tsx';
import { getExportItems, sortConnectAppsInExportItems } from './utils.tsx';

const EXPORT_WITH_SCOPE = [
	'CSV_CURRENT_FIELDS',
	'CSV_WITH_BOM_CURRENT_FIELDS',
	'CSV_FILTER_DEFAULT_FIELDS',
];

export const ExportIssues: FC<ExportIssuesProps> = ({
	showEcosystemApps = true,
	browserMetricsKey = 'unknown',
	jql,
	filterId,
	isModifiedFilter,
	filterName,
	customTriggerButton,
	placement,
	isHideDoneWorkItemsEnabled,
	isInlineFieldConfigEnabled,
}: ExportIssuesProps) => {
	// This should be updated in the future when we're managing filter state more explicitly.
	const hasCustomFilterJqlBeenModified = isCustomFilter(filterId || '') && isModifiedFilter;

	const { fetch, exportItems, loading, error } = useFetchAllExportIssuesResources(
		jql,
		filterId,
		hasCustomFilterJqlBeenModified,
	);
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const exportButtonRef = useRef<HTMLElement | null>(null);
	const [openExportIssuesInteraction] = useState(
		metrics.interaction({
			key: `${browserMetricsKey}.load-export-issues-dropdown.start-end.diff`,
		}),
	);
	const { locale } = useTenantContext();
	const { current: intlCollator } = useRef(new Intl.Collator(standardizeLocale(locale)));
	const filteredExportItems = getExportItems(exportItems) || [];
	// Alphabetically sort the connect apps in the export issues dropdown.
	const orderedExportItems =
		(showEcosystemApps && sortConnectAppsInExportItems(filteredExportItems, intlCollator)) ||
		filteredExportItems;

	/** We only want to count the number of apps added to the exports dropdown */
	const appCount = orderedExportItems[ECOSYSTEM_APPS_GROUP_INDEX]?.length ?? 0;
	const projectContext = fg('jsc_project_scoped_column_config_for_csv_export')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useProjectContext()
		: { data: null };
	const {
		isOpen: isCreateDashboardGadgetModalOpen,
		openModal: openCreateDashboardGadgetModal,
		closeModal: closeCreateDashboardGadgetModal,
		clearAndNavigateToDashboard,
	} = useCreateDashboardGadgetModalControls();

	const onOpen = () => {
		/* We don't want to refetch if the last used jql is
		 * the same as the active jql.
		 */
		if (!isOpen) {
			openExportIssuesInteraction.start();
			fetch(false);
		}
		setIsOpen(!isOpen);
	};

	!loading && openExportIssuesInteraction.stop();

	const exportOptionHeaderLabel = exportItems?.viewOptionHeader?.label;

	const commonInput = useMemo<AsyncExportIssuesCommonInput>(() => {
		const validFilterId = isFilterId(filterId);

		return {
			jql,
			modified: isModifiedFilter,
			...(validFilterId && { filterId }),
			...(fg('jsc_exports_hide_done_work_items') && {
				excludeDoneWorkItems: isHideDoneWorkItemsEnabled ?? false,
			}),
		};
	}, [filterId, isHideDoneWorkItemsEnabled, isModifiedFilter, jql]);

	const isVREnabled = isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons');

	return (
		<ExportIssuesState scope="ExportIssuesState">
			<DropdownMenu
				{...(!isVREnabled &&
					fg('render_export_issues_menu_to_parent') && { shouldRenderToParent: true })}
				isLoading={loading}
				onOpenChange={onOpen}
				testId="issue-navigator-action-export-issues.ui.filter-button"
				trigger={({ triggerRef, ...props }) => {
					const combinedRef = fg('endeavour_gadget_cdg_export_focus')
						? (element: HTMLElement | null) => {
								exportButtonRef.current = element;
								// Call the original triggerRef if it's a function
								if (typeof triggerRef === 'function') {
									triggerRef(element);
								}
							}
						: triggerRef;
					return customTriggerButton ? (
						customTriggerButton(isOpen, { triggerRef: combinedRef, ...props })
					) : (
						<FilterButton
							customTriggerProps={{
								...props,
								ref: combinedRef,
							}}
							text={formatMessage(messages.exportButtonText)}
							label=""
							isSelected={isOpen}
							vcId="export-issues"
						/>
					);
				}}
				shouldFlip
				placement={placement || 'bottom-end'}
				spacing="compact"
			>
				{!error && exportOptionHeaderLabel ? (
					<>
						<Section testId="issue-navigator-action-export-issues.ui.nso-bulk-export-info">
							<Box paddingInline="space.150" paddingBlock="space.100" xcss={styles}>
								<Text color="color.text.subtle" size="small">
									{exportOptionHeaderLabel}
								</Text>
							</Box>
						</Section>
						<Section hasSeparator>{null}</Section>
					</>
				) : null}
				{!error ? (
					orderedExportItems.map((group, index) =>
						showEcosystemApps || index !== ECOSYSTEM_APPS_GROUP_INDEX ? (
							<StyledDropDownItemGroup key={`export-item-group-${index}`}>
								<DropdownItemGroup>
									{group.map((option, dropdownItemIndex) => {
										let addtlInput = {};
										if (
											isAsyncExportItem(option) &&
											EXPORT_WITH_SCOPE.includes(option.exportType) &&
											isInlineFieldConfigEnabled &&
											projectContext?.data?.projectKey != null &&
											fg('jsc_project_scoped_column_config_for_csv_export')
										) {
											addtlInput = {
												scope: {
													operationScope: getOperationScope(projectContext?.data?.projectKey),
													projectKey: projectContext?.data?.projectKey,
												},
											};
										}

										return (
											<ContextualAnalyticsData
												key={
													(index === ECOSYSTEM_APPS_GROUP_INDEX &&
														`export-item-app-${dropdownItemIndex}`) ||
													option.id
												}
												attributes={{
													exportId: option.id,
													exportLabel: option.label,
													...(isAsyncExportItem(option)
														? {
																exportType: option.exportType,
																maxResults: option.maxResults,
																fileExtension: option.fileExtension,
															}
														: {}),
												}}
											>
												<ExportItem
													exportItem={option}
													appCount={appCount}
													commonInput={{ ...commonInput, ...addtlInput }}
													onOpenCreateDashboardGadgetModal={openCreateDashboardGadgetModal}
												/>
											</ContextualAnalyticsData>
										);
									})}
								</DropdownItemGroup>
							</StyledDropDownItemGroup>
						) : null,
					)
				) : (
					<ErrorState onRetry={() => fetch(true)} pageAction="exportIssues" />
				)}
			</DropdownMenu>
			{isCreateDashboardGadgetModalOpen ? (
				<AsyncCreateDashboardGadgetModal
					{...getCreateDashboardGadgetModalParams(
						jql,
						filterId,
						isCustomFilter,
						hasCustomFilterJqlBeenModified,
					)}
					shouldReturnFocus={fg('endeavour_gadget_cdg_export_focus') ? exportButtonRef : undefined}
					onCancel={closeCreateDashboardGadgetModal}
					onSuccess={clearAndNavigateToDashboard}
					filterName={filterName}
				/>
			) : null}
		</ExportIssuesState>
	);
};

export default ExportIssues;

export const ExportIssuesDropdownWithErrorBoundary: FC<ExportIssuesProps> = (
	props: ExportIssuesProps,
) => {
	const [resetCaughtError, setResetCaughtError] = useState<Error>();

	return (
		<JSErrorBoundary
			packageName="jiraIssueNavigatorActionExportIssues"
			id="sync-export-issues"
			sendToPrivacyUnsafeSplunk
			fallback="flag"
			onError={() => setResetCaughtError(undefined)}
			resetCaughtError={resetCaughtError}
		>
			<ExportIssues {...props} />
		</JSErrorBoundary>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropDownItemGroupOld = styled.div({
	marginTop: token('space.200'),
});
const styles = xcss({ maxWidth: '14rem' });

const dividerStyles = xcss({
	borderBottomStyle: 'solid',
	borderBottomWidth: 'border.width',
	borderBottomColor: 'color.border',
});

const StyledDropDownItemGroup = componentWithCondition(
	() => isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons'),
	({ children, key }: PropsWithChildren<{ key: string }>) => (
		<Box key={key} xcss={dividerStyles}>
			{children}
		</Box>
	),
	StyledDropDownItemGroupOld,
);
